import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Text,
  Stack,
  Skeleton,
  Table
} from '@efast_public/fjd-component-library';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  WertelistenEintrag,
  GovOsResult,
  OriginalValuesResponse
} from '../api/govos.types';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { useNotification } from '../shared/notification/useNotification';

interface OriginalValuesModalProps {
  isOpen: boolean;
  onClose: () => void;
  feldId: string;
  feldName: string;
  startAppId: number;
  processId: number;
}

export function OriginalValuesModal({
  isOpen,
  onClose,
  feldId,
  feldName,
  startAppId,
  processId
}: OriginalValuesModalProps) {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();

  const { data: originalValuesData, isLoading } =
    useQuery<OriginalValuesResponse>({
      queryKey: ['originalValues', startAppId, processId, feldId],
      queryFn: async () => {
        const response = await govosClient.get<OriginalValuesResponse>(
          GovosApiRoutes.ueberschreibbareFelder(startAppId, processId, feldId)
        );
        return response.data;
      },
      enabled: isOpen,
      refetchOnMount: 'always',
      staleTime: 0
    });

  const { mutate: resetToOriginal, isPending: isResetting } = useMutation({
    mutationFn: async () => {
      return notifyUserFromRequest(
        () =>
          govosClient.put<GovOsResult<unknown>>(
            GovosApiRoutes.updateUeberschreibbareFelder(
              startAppId,
              processId,
              feldId
            ),
            {
              reset: true
            }
          ),
        {
          successMessage: t('OriginalValuesModal.resetSuccess'),
          errorMessage: t('OriginalValuesModal.resetError'),
          loadingMessage: t('OriginalValuesModal.resetLoading')
        }
      );
    },
    onSuccess: async () => {
      // Manually update the cache with reset data for immediate UI update
      if (originalValuesData && originalValuesData.result === 'success') {
        const resetData = {
          ...originalValuesData,
          feld: {
            ...originalValuesData.feld,
            ueberschriebeneWerte: originalValuesData.feld?.originalWerte,
            istUeberschrieben: true
          }
        };
        queryClient.setQueryData(
          [
            GovosApiRoutes.ueberschreibbareFelder(startAppId, processId, feldId)
          ],
          resetData
        );
      }
      // Also invalidate to ensure backend state is synced
      await queryClient.invalidateQueries({
        queryKey: [
          GovosApiRoutes.ueberschreibbareFelder(startAppId, processId, feldId)
        ]
      });
      await queryClient.invalidateQueries({
        queryKey: [GovosApiRoutes.configuredOnlineDienst(processId)]
      });
      onClose();
    }
  });

  const originalWerte =
    originalValuesData?.result === 'success' &&
    originalValuesData?.feld?.originalWerte
      ? originalValuesData.feld.originalWerte
      : [];

  return (
    <Modal open={isOpen} onClose={onClose} size={2}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('OriginalValuesModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <Stack spacing="l" orientation="vertical">
          {isLoading ? (
            <Stack spacing="xxl" orientation="vertical">
              <Skeleton height={40} />
              <Skeleton height={200} />
              <Skeleton height={300} />
            </Stack>
          ) : (
            <>
              <Stack spacing="s" direction="row">
                <Text strong>{t('OriginalValuesModal.feldName')}</Text>
                <Text>{feldName}</Text>
              </Stack>

              <Stack spacing="m" orientation="vertical">
                {originalWerte.length > 0 ? (
                  <Table style={{ width: '100%' }}>
                    <Table.Columns sticky>
                      <Table.Column value="code">
                        {t('OriginalValuesModal.codeColumn')}
                      </Table.Column>
                      <Table.Column value="name">
                        {t('OriginalValuesModal.nameColumn')}
                      </Table.Column>
                      <Table.Column value="hilfetext">
                        {t('OriginalValuesModal.hilfetextColumn')}
                      </Table.Column>
                    </Table.Columns>
                    <Table.Rows>
                      {originalWerte.map(
                        (wert: WertelistenEintrag, index: number) => (
                          <Table.Row key={index}>
                            <Table.Cell>
                              <Text strong>{wert.code}</Text>
                            </Table.Cell>
                            <Table.Cell>{wert.name}</Table.Cell>
                            <Table.Cell>{wert.hilfetext}</Table.Cell>
                          </Table.Row>
                        )
                      )}
                    </Table.Rows>
                  </Table>
                ) : (
                  <Text variant="secondary">
                    {t('OriginalValuesModal.noValues')}
                  </Text>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}
        >
          <Button variant="primary" onClick={onClose}>
            {t('OriginalValuesModal.closeButton')}
          </Button>
          <Button
            variant="secondary-outline"
            onClick={() => resetToOriginal()}
            disabled={isResetting}
          >
            {t('OriginalValuesModal.resetButton')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
